import {Injectable, Optional} from '@angular/core';
import {HttpClient, HttpEventType, HttpHeaders, HttpParams, HttpRequest} from '@angular/common/http';
import {from, Observable, Subject} from 'rxjs';
import {Blob} from '@paperlessio/sdk/api/models';
import {environment} from '@environment';
import {ParallelHasher} from 'ts-md5/dist/parallel_hasher';
import {catchError, concatMap, filter, map, tap} from 'rxjs/operators';
import {SessionStore} from 'projects/submission/src/app/session/session.store';
import {DomSanitizer} from '@angular/platform-browser';

@Injectable({providedIn: 'root'})
export class UploadService {
  public BASE_URL = environment.api.core;
  public progress = new Subject<Blob>();

  private uploading = new Set<Blob>();

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    @Optional() private sessionStore: SessionStore
  ) { }

  private request(blob: Blob): Observable<Blob> {
    let url: string;
    let responseBody: any;

    const submission_id = this.sessionStore?.submission_id;
    if (submission_id) {
      responseBody = {
        ...blob.toDirectUploadJSON(),
        submission_id
      };

      url = `${this.BASE_URL}v1/participation/blobs`;
    } else {
      responseBody = blob.toDirectUploadJSON();
      url = `${this.BASE_URL}v1/blobs`;
    }

    return this.http.post<Blob>(url, responseBody);
  }

  private processUpload(blob: Blob): Observable<any> {
    const params = new HttpParams();

    const options = {
      params,
      reportProgress: true,
      observe: 'events',
      headers: new HttpHeaders(blob.direct_upload.headers as any).append('skip-auth', 'true') // this is for AWS S3 - NOT FOR US!!
    };

    blob.direct_upload.url = blob.direct_upload.url.replace('http://127.0.0.1:3001/', '/');
    // external url test https://httpbin.org/put as second param
    const req = new HttpRequest('PUT', blob.direct_upload.url, blob.file, options);
    return this.http.request(req);
  }

  public upload(blob: Blob): Observable<Blob> {
    blob.uploadTrustedLocalFileUrl = this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(blob.file));
    const hasher = new ParallelHasher('/assets/md5_worker.js');

    this.processStatus(blob, 5);

    return from(hasher.hash(blob.file))
      .pipe(
        concatMap((hash: string) => {
          blob.checksum = btoa(hash.match(/\w{2}/g).map(a => String.fromCharCode(parseInt(a, 16))).join(''));

          this.processStatus(blob, 10);

          // get direct upload url from server
          return this.request(blob)
            .pipe(
              tap((resBlob: Blob) => {
                blob.id = resBlob.id;
                blob.signed_id = resBlob.signed_id;
                blob.direct_upload = resBlob.direct_upload;
              }),
              // upload to S3
              concatMap(_ => this.processUpload(blob).pipe(
                tap(uploaded => {
                  if (uploaded.type === HttpEventType.UploadProgress) {
                    this.processStatus(blob, 10 + 0.85 * Math.round(100 * (uploaded.loaded / uploaded.total)));
                  }

                  if (uploaded.type === HttpEventType.Response && uploaded?.url) {
                    this.processStatus(blob, 100);
                  }
                }),
                catchError(err => {
                  blob.uploadError = err;
                  this.processStatus(blob, 100);
                  throw err;
                }),
                filter(uploaded => (uploaded.type === HttpEventType.Response && uploaded?.url)),
                map(_ => blob),
              ))
            );
        }));
  }

  public isUploading(): boolean {
    return this.uploading.size > 0;
  }

  public resetUpload(blob: Blob): void {
    this.uploading.delete(blob);
  }

  private processStatus(blob: Blob, percent: number): void {
    blob.uploadPercentage = percent;

    if (percent === 100) {
      this.uploading.delete(blob);
    } else {
      this.uploading.add(blob);
    }

    this.progress.next(blob);
  }
}
